import React, { useMemo } from "react";
import { Table, Button, Menu, Box, Grid, Divider } from "@mantine/core";
import Text from "@/components/atoms/Text";
import Group from "@/components/atoms/Group";
import { useReactTable, getCoreRowModel, flexRender, ColumnDef } from "@tanstack/react-table";
import styles from "./styles.module.css";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import CTA from "@/components/atoms/CTA";
import Accordion from "@/components/atoms/accordions/TableRowMobileAccordion";

interface Props<TData extends object> {
  data: TData[];
  columns: ColumnDef<TData>[];
  accordionTriggerContentIndex?: number;
  editActions?: (row: TData) => { label: string; action: (row: TData) => void }[];
  isAdmin: boolean;
  /** Greys out deactivated rows. Defaults to rows with `deleted: true`. */
  isRowInactive?: (row: TData) => boolean;
}

const isDeletedRow = (row: object) => (row as { deleted?: boolean }).deleted === true;

const SettingsTable = <TData extends object>({
  data,
  columns,
  accordionTriggerContentIndex,
  editActions,
  isAdmin,
  isRowInactive = isDeletedRow,
}: Props<TData>) => {
  const tableColumns = useMemo(() => {
    return [
      ...columns,
      {
        id: "actions",
        header: () => "",
        cell: ({ row }) => {
          return (
            <div className={styles.actionMenu}>
              {isAdmin && editActions ? (
                <Menu
                  offset={-5}
                  position="bottom-end"
                  shadow="md"
                  closeOnEscape={true}
                  withArrow={true}
                  arrowOffset={40}
                >
                  <Menu.Target>
                    <Button variant="transparent">
                      <SVGAsset name="dotdotdot-grey" className="open-context-panel" />
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {editActions(row.original).map((action, i) => (
                      <Menu.Item
                        key={i}
                        style={{ width: "auto" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          action.action(row.original);
                        }}
                      >
                        {action.label}
                      </Menu.Item>
                    ))}
                  </Menu.Dropdown>
                </Menu>
              ) : // TODO: SVG som var her tuller når det ikke er noen extra actions
              null}
            </div>
          );
        },
      },
    ];
  }, [columns, editActions, isAdmin]);

  const table = useReactTable({
    data: data ?? ([] as TData[]),
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Box className={styles.settingsTableRoot}>
      <Table className={styles.desktopTable} withTableBorder withRowBorders>
        <Table.Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Table.Th key={header.id}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </Table.Th>
              ))}
            </Table.Tr>
          ))}
        </Table.Thead>
        <Table.Tbody>
          {table.getRowModel().rows.map((row) => {
            return (
              <Table.Tr key={row.id} className={isRowInactive(row.original) ? styles.deactivatedRow : undefined}>
                {row.getVisibleCells().map((cell) => (
                  <Table.Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Table.Td>
                ))}
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>

      {/* Mobile Accordion View */}
      <Box className={styles.mobileAccordion}>
        <Accordion unstyled={true}>
          {table.getRowModel().rows.map((row) => {
            const triggerCell = row.getVisibleCells()[accordionTriggerContentIndex ?? 0];
            return (
              <Accordion.Item key={`item-${row.id}`} value={`item-${row.id}`}>
                <Accordion.Control>
                  <Text size="sm">
                    {flexRender(triggerCell.column.columnDef.cell, triggerCell.getContext())
                      ? flexRender(triggerCell.column.columnDef.cell, triggerCell.getContext())
                      : (table.getRowModel().rows.indexOf(row) + 1).toString().padStart(2, "0")}
                  </Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Grid>
                    {row.getVisibleCells().map((cell, j) => {
                      const header = table.getHeaderGroups()[0].headers.find((h) => h.id === cell.column.id);
                      if (cell.column.id === "actions") {
                        // Same rule as the desktop menu: only admins get the row actions.
                        if (!isAdmin || !editActions) return null;
                        const actions = editActions(row.original);
                        return (
                          <React.Fragment key={`${row.id}-actions-${j}`}>
                            <Grid.Col span={12}>
                              <Divider />
                            </Grid.Col>
                            <Grid.Col span={12}>
                              <Group gap="8px" w={"100%"} justify="flex-end">
                                {actions.map((action, k) => (
                                  <CTA
                                    intent={k === 0 ? "primary" : "secondary"}
                                    key={`${row.id}-cta-${k}-${j}-${String(action.label)}`}
                                    onClick={() => action.action(row.original)}
                                    variant="outline"
                                    color="gray"
                                  >
                                    {action.label}
                                  </CTA>
                                ))}
                              </Group>
                            </Grid.Col>
                          </React.Fragment>
                        );
                      }

                      return (
                        <Grid.Col key={`${row.id}-${cell.id}-${j}`} span={12}>
                          <Group justify="space-between">
                            {header && (
                              <Text c={"dimmed"} size="sm">
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </Text>
                            )}
                            <Text size={"sm"} span={true}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </Text>
                          </Group>
                        </Grid.Col>
                      );
                    })}
                  </Grid>
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </Box>
    </Box>
  );
};

export default SettingsTable;
